import { Link } from 'react-router';
import { tx } from '../i18n';
import { bilingue } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando, hora, miles, soloDia } from '../lib/formato';
import { RadarDelParque } from '../tablero/RadarDelParque';
import type { ApplianceFila } from '../tipos';
import { Cabecera, Consulta, Pastilla, Tabla } from '../ui/piezas';

interface Aviso {
  appliance_id: string;
  appliance: string;
  organizacion: string;
  tipo: string;
  gravedad: 'grave' | 'aviso';
  en: string;
  es: string;
}

interface DatosTablero {
  parque: { total: number; en_linea: number; sin_contacto: number; nunca: number; suspendidos: number; esperando_alta: number };
  equipos: { total: number; caidos: number; appliances: number };
  licencias: { vigentes: number; por_vencer: number; vencidas: number; sin_licencia: number };
  proximas: { appliance_id: string; appliance: string; organizacion: string; vence: string; dias: number; equipos: number | null }[];
  versiones: { ultima_estable: string | null; por_version: { version: string | null; cuantos: number }[] };
  clientes: { organizacion_id: string; organizacion: string; appliances: number; equipos: number; caidos: number; consentimiento: string | null }[];
  actividad: { tiempo: string; usuario: string; accion: string; objeto_id: string | null; appliance: string | null }[];
  avisos: Aviso[];
}

/**
 * El color de una versión del parque: la última estable está bien, otra quedó atrás. Sin
 * ninguna estable publicada no hay con qué comparar, así que ninguna es "vieja".
 */
function tonoVersion(version: string | null, estable: string | null): string {
  if (!version) return 'desconocido';
  if (!estable) return 'neutro';
  return version === estable ? 'arriba' : 'parcial';
}

/** El color de una proporción de equipos caídos: el mismo criterio que en el appliance. */
function tonoCaidos(caidos: number, total: number): 'arriba' | 'parcial' | 'caido' {
  if (caidos === 0) return 'arriba';
  return caidos / Math.max(1, total) > 0.05 ? 'caido' : 'parcial';
}

/**
 * Una cifra del parque: el número grande, qué es, y debajo una barra con la proporción que
 * ese número esconde (en línea contra el total, caídos contra el total). La barra es lo que
 * hace que un "98" signifique algo sin tener que leer nada más.
 */
function Cifra({
  valor,
  titulo,
  detalle,
  partes,
  tono = 'neutro',
}: {
  valor: string;
  titulo: string;
  detalle?: string;
  partes?: { n: number; tono: string }[];
  tono?: string;
}) {
  const total = Math.max(
    1,
    (partes ?? []).reduce((s, p) => s + p.n, 0),
  );
  return (
    <div className={`kpi ${tono}`}>
      <div className="kpi-n num">{valor}</div>
      <div className="kpi-t">{titulo}</div>
      {detalle && <div className="kpi-d">{detalle}</div>}
      {partes && partes.length > 0 && (
        <div className="kpi-barra" aria-hidden="true">
          {partes
            .filter((p) => p.n > 0)
            .map((p, i) => (
              <i key={i} className={p.tono} style={{ width: `${(p.n / total) * 100}%` }} />
            ))}
        </div>
      )}
    </div>
  );
}

export function Tablero() {
  // El tablero suele quedar abierto en una pantalla del NOC: se refresca solo.
  const q = useConsulta<DatosTablero>('/tablero', { refetchInterval: 60_000 });
  // el radar dibuja un eco por appliance: necesita la lista, no los totales
  const lista = useConsulta<ApplianceFila[]>('/appliances', { refetchInterval: 60_000 });
  const d = q.data;
  const t = tx.tablero;

  const atrasados = d
    ? d.versiones.por_version.filter((v) => d.versiones.ultima_estable && v.version !== d.versiones.ultima_estable).reduce((s, v) => s + v.cuantos, 0)
    : 0;

  return (
    <div className="pagina">
      <Cabecera titulo={t.titulo} accion={<span className="d">{t.alDia(hora(new Date()))}</span>}>
        {t.que}
      </Cabecera>

      <Consulta q={q}>
        {d && (
          <>
            <div className="kpis">
              <Cifra
                valor={`${miles(d.parque.en_linea)}/${miles(d.parque.total)}`}
                titulo={t.applianceEnLinea}
                detalle={[
                  d.parque.sin_contacto > 0 ? t.sinContactoN(d.parque.sin_contacto) : null,
                  d.parque.esperando_alta > 0 ? t.esperandoAltaN(d.parque.esperando_alta) : null,
                  d.parque.suspendidos > 0 ? t.suspendidosN(d.parque.suspendidos) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                partes={[
                  { n: d.parque.en_linea, tono: 'arriba' },
                  { n: d.parque.sin_contacto, tono: 'caido' },
                  { n: d.parque.esperando_alta + d.parque.suspendidos, tono: 'desconocido' },
                ]}
                tono={d.parque.sin_contacto > 0 ? 'caido' : 'arriba'}
              />
              <Cifra
                valor={miles(d.equipos.total)}
                titulo={t.equiposMonitoreados}
                detalle={d.equipos.caidos > 0 ? t.equiposCaidosN(miles(d.equipos.caidos)) : t.todosArriba}
                partes={[
                  { n: d.equipos.total - d.equipos.caidos, tono: 'arriba' },
                  { n: d.equipos.caidos, tono: 'caido' },
                ]}
                tono={tonoCaidos(d.equipos.caidos, d.equipos.total)}
              />
              <Cifra
                valor={miles(d.licencias.vigentes + d.licencias.por_vencer)}
                titulo={t.licenciasActivas}
                detalle={
                  [
                    d.licencias.por_vencer > 0 ? t.porVencerN(d.licencias.por_vencer) : null,
                    d.licencias.vencidas > 0 ? t.vencidasN(d.licencias.vencidas) : null,
                    d.licencias.sin_licencia > 0 ? t.sinLicenciaN(d.licencias.sin_licencia) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || t.todasAlDia
                }
                partes={[
                  { n: d.licencias.vigentes, tono: 'arriba' },
                  { n: d.licencias.por_vencer, tono: 'parcial' },
                  { n: d.licencias.vencidas + d.licencias.sin_licencia, tono: 'caido' },
                ]}
                tono={d.licencias.vencidas > 0 ? 'caido' : d.licencias.por_vencer > 0 ? 'parcial' : 'arriba'}
              />
              <Cifra
                valor={d.versiones.ultima_estable ?? '—'}
                titulo={t.ultimaEstable}
                detalle={d.versiones.ultima_estable ? (atrasados > 0 ? t.atrasadosN(atrasados) : t.todosAlDiaVersion) : t.sinVersiones}
                partes={[
                  { n: Math.max(0, d.parque.total - atrasados), tono: 'arriba' },
                  { n: atrasados, tono: 'parcial' },
                ]}
                tono={atrasados > 0 ? 'parcial' : 'arriba'}
              />
            </div>

            <div className="tablero-grid tablero-radar">
              <section className="panel panel-radar">
                <h2 className="rotulo">
                  {t.radar}
                  <span className="aux">{t.alDia(hora(new Date()))}</span>
                </h2>
                <RadarDelParque appliances={lista.data ?? []} />
              </section>

              <section className="panel panel-avisos">
                <h2 className="rotulo">
                  {t.paraMirar}
                  {d.avisos.length > 0 && <span className="aux">{d.avisos.length}</span>}
                </h2>
                {d.avisos.length === 0 ? (
                  <div className="todo-bien">
                    <b>{t.todoEnOrden}</b>
                    <p>{t.todoEnOrdenQue(miles(d.parque.en_linea), miles(d.equipos.total))}</p>
                  </div>
                ) : (
                  <ul className="avisos">
                    {d.avisos.map((a, i) => (
                      <li key={`${a.appliance_id}-${a.tipo}-${i}`} className={a.gravedad}>
                        <span className="avisos-punto" aria-hidden="true" />
                        <div className="avisos-que">
                          <Link to={`/appliances/${a.appliance_id}`}>{a.appliance}</Link>
                          <small>{a.organizacion}</small>
                        </div>
                        <p>{bilingue(a) ?? a.en}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <div className="tablero-grid">
              <section className="panel">
                <h2 className="rotulo">{t.renovaciones}</h2>
                {d.proximas.length === 0 ? (
                  <p className="nota">{t.sinRenovaciones}</p>
                ) : (
                  <ul className="renovaciones">
                    {d.proximas.map((p) => (
                      <li key={p.appliance_id}>
                        <div>
                          <Link to={`/appliances/${p.appliance_id}`}>{p.organizacion}</Link>
                          <small>
                            {p.appliance}
                            {p.equipos ? ` · ${miles(p.equipos)} ${tx.licencia.equiposCortos}` : ''}
                          </small>
                        </div>
                        <Pastilla tono={p.dias < 0 ? 'caido' : p.dias <= 15 ? 'parcial' : 'desconocido'}>
                          {p.dias < 0 ? t.vencidaHace(-p.dias) : t.enDias(p.dias)}
                        </Pastilla>
                        <span className="d">{soloDia(p.vence)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="panel">
                <h2 className="rotulo">{t.porVersion}</h2>
                {d.versiones.por_version.length === 0 ? (
                  <p className="nota">{t.sinVersion}</p>
                ) : (
                  <>
                    <div className="pila" aria-hidden="true">
                      {d.versiones.por_version.map((v) => (
                        <i
                          key={v.version ?? 'sin'}
                          className={tonoVersion(v.version, d.versiones.ultima_estable)}
                          style={{ width: `${(v.cuantos / Math.max(1, d.parque.total)) * 100}%` }}
                        />
                      ))}
                    </div>
                    <ul className="leyenda-versiones">
                      {d.versiones.por_version.map((v) => (
                        <li key={v.version ?? 'sin'}>
                          <span className={`punto ${tonoVersion(v.version, d.versiones.ultima_estable)}`} />
                          <b className="num">{v.version ?? tx.comun.noReportada}</b>
                          <span className="d">{v.cuantos}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            </div>

            <div className="tablero-grid">
              <section className="panel">
                <h2 className="rotulo">{t.clientes}</h2>
                {d.clientes.length === 0 ? (
                  <p className="nota">{t.sinClientes}</p>
                ) : (
                  <Tabla>
                    <thead>
                      <tr>
                        <th>{tx.comun.organizacion}</th>
                        <th className="der">{tx.comun.appliances}</th>
                        <th className="der">{tx.comun.equipos}</th>
                        <th className="der">{tx.appliances.caidos}</th>
                        <th>{t.salud}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.clientes.map((c) => (
                        <tr key={c.organizacion_id}>
                          <td>
                            {c.organizacion}
                            {c.consentimiento && c.consentimiento !== 'ninguno' && <small> · {t.compartida}</small>}
                          </td>
                          <td className="der num">{c.appliances}</td>
                          <td className="der num">{miles(c.equipos)}</td>
                          <td className={`der num${c.caidos > 0 ? ' mal' : ''}`}>{c.caidos || '—'}</td>
                          <td>
                            <div className="barra-salud" aria-hidden="true">
                              <i className="arriba" style={{ width: `${((c.equipos - c.caidos) / Math.max(1, c.equipos)) * 100}%` }} />
                              <i className="caido" style={{ width: `${(c.caidos / Math.max(1, c.equipos)) * 100}%` }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tabla>
                )}
              </section>

              <section className="panel">
                <h2 className="rotulo">{t.actividad}</h2>
                {d.actividad.length === 0 ? (
                  <p className="nota">{t.sinActividad}</p>
                ) : (
                  <ul className="actividad">
                    {d.actividad.map((a, i) => (
                      <li key={`${a.tiempo}-${i}`}>
                        <span className="d">{cuando(a.tiempo)}</span>
                        <b>{tx.acciones[a.accion] ?? a.accion}</b>
                        <span className="actividad-quien">
                          {a.appliance && a.objeto_id ? <Link to={`/appliances/${a.objeto_id}`}>{a.appliance}</Link> : null} {a.usuario}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </Consulta>
    </div>
  );
}
